import { Box } from '@mui/material'
import { type RCNPD } from '../../../../../models/RCNPD'
import { SubBasicTable, type SubBasicTableColumn, type SubBasicTableRow } from '../SubBasicTable'
import { type Dispatch } from 'react'
import { type RCNPDSeriesSession } from '../../../../../models/RCNPDSeriesSession'

interface Props {
  readonly: boolean
  trackingReadonly: boolean
  training: RCNPD
  onEditSingleTrainingAdditionalDetails: (training: RCNPD) => void
  onEditSingleTrainingTracking: (training: RCNPD) => void
  setSingleTrainingWithAdditionalDetailsToDelete: Dispatch<React.SetStateAction<RCNPD | null>>
  setSingleTrainingWithTrackingToDelete: Dispatch<React.SetStateAction<RCNPD | null>>

  onEditSeriesSessionAdditionalDetails: (session: RCNPDSeriesSession) => void
  onEditSeriesSessionTracking: (session: RCNPDSeriesSession) => void
  setSeriesSessionAdditionalDetailsToDelete: (session: RCNPDSeriesSession) => void
  setSeriesSessionTrackingToDelete: Dispatch<React.SetStateAction<RCNPDSeriesSession | null>>
}
export const TrainingExpandedContent = (props: Props): JSX.Element | null => {
  const { training } = props

  if (training.isUpdate !== true) return null
  if (training.isSeries && (training.seriesSessions?.length ?? 0) < 1) return null

  const seriesExpandedContent = (): JSX.Element => {
    const hasTrackingDetails = training.seriesSessions?.some(s => s.trainingHeld != null || s.numberOfAttendees != null || s.numberOfTeamsTrained != null || s.budgetSpent != null) ?? false
    const columns: SubBasicTableColumn[] = [
      { id: 'series-name', label: 'Session Name', minWidth: 100 },
      { id: 'series-focus', label: 'Focus' },
      { id: 'series-date', label: 'Date', minWidth: 75 },
      { id: 'series-length', label: 'Length', minWidth: 100 },
      { id: 'series-budget', label: 'Budget', minWidth: 50 },
      { id: 'action', label: '' },
      { id: 'series-trainingHeld', label: 'Held', isTrackingColumn: true },
      { id: 'series-numberOfAttendees', label: 'Attendees', isTrackingColumn: true },
      { id: 'series-numberOfTeamsTrained', label: 'Teams', isTrackingColumn: true },
      { id: 'series-budgetSpent', label: 'Spent', isTrackingColumn: true },
      { id: 'trackingAction', label: '', isTrackingColumn: true },
      { id: 'spacer', label: '', isTrackingColumn: true }
    ]
    const rows: SubBasicTableRow[] = [
      ...(training.seriesSessions ?? []).sort((a, b) => new Date(a.date ?? '').getTime() - new Date(b.date ?? '').getTime()).map(session => {
        return {
          ...session, // Pass the entire object for editing
          id: session.id,
          'series-name': session.name,
          'series-focus': session.contentFocus?.join(', '),
          'series-date': session.date != null ? new Date(session.date).toLocaleDateString('en-US') : '',
          'series-length': session.length,
          'series-budget': `$${Number(session.budget).toLocaleString()}`,
          action: '',
          'series-trainingHeld': session.trainingHeld === true ? 'Yes' : 'No',
          'series-numberOfAttendees': session.numberOfAttendees,
          'series-numberOfTeamsTrained': session.numberOfTeamsTrained,
          'series-budgetSpent': `$${Number(session.budgetSpent).toLocaleString()}`,
          trackingAction: '',
          spacer: ''
        }
      })
    ]
    return (
      <Box
        sx={{
          p: '2rem',
          backgroundColor: 'var(--grey-100)'
        }}
      >
        <SubBasicTable
          columns={columns}
          rows={rows}
          readonly={props.readonly}
          trackingReadonly={props.trackingReadonly}
          hasTrackingDetails={hasTrackingDetails}
          onEditAdditionalDetails={props.onEditSeriesSessionAdditionalDetails}
          onDeleteAdditionalDetails={props.setSeriesSessionAdditionalDetailsToDelete}
          onEditTracking={props.onEditSeriesSessionTracking}
          onDeleteTracking={props.setSeriesSessionTrackingToDelete}
        />
      </Box>
    )
  }

  const singleExpandedContent = (): JSX.Element | null => {
    const hasAdditionalDetails = (training.contentFocus != null && training.contentFocus.length > 0) && training.trainingDate != null && training.trainingLength != null
    const hasTrackingDetails = training.trainingHeld != null || training.numberOfAttendees != null || training.actualBudgetAmount != null
    if (!hasAdditionalDetails) return null
    const columns: SubBasicTableColumn[] = [
      { id: 'single-focus', label: 'Focus' },
      { id: 'single-trainingDate', label: 'Date', minWidth: 75 },
      { id: 'single-trainingLength', label: 'Length', minWidth: 100 },
      { id: 'single-budget', label: 'Budget', minWidth: 50 },
      { id: 'action', label: '' },
      { id: 'single-trainingHeld', label: 'Held', isTrackingColumn: true },
      { id: 'single-numberOfAttendees', label: 'Attendees', isTrackingColumn: true },
      { id: 'single-actualBudgetAmount', label: 'Spent', isTrackingColumn: true },
      { id: 'trackingAction', label: '', isTrackingColumn: true },
      { id: 'spacer', label: '', isTrackingColumn: true }
    ]
    const rows: SubBasicTableRow[] = [
      {
        ...training, // Pass the entire object for editing
        id: training.id,
        'single-focus': training.contentFocus?.join(', '),
        'single-trainingDate': training.trainingDate != null ? new Date(training.trainingDate).toLocaleDateString('en-US') : '',
        'single-trainingLength': training.trainingLength,
        'single-budget': `$${Number(training.budgetRequest).toLocaleString()}`,
        action: '',
        'single-trainingHeld': training.trainingHeld === true ? 'Yes' : 'No',
        'single-numberOfAttendees': training.numberOfAttendees,
        'single-actualBudgetAmount': `$${Number(training.actualBudgetAmount).toLocaleString()}`,
        trackingAction: '',
        spacer: ''
      }
    ]
    return (
      <Box
        sx={{
          p: '2rem',
          backgroundColor: 'var(--grey-100)'
        }}
      >
        <SubBasicTable
          columns={columns}
          rows={rows}
          readonly={props.readonly}
          trackingReadonly={props.trackingReadonly}
          hasTrackingDetails={hasTrackingDetails}
          onEditAdditionalDetails={props.onEditSingleTrainingAdditionalDetails}
          onDeleteAdditionalDetails={props.setSingleTrainingWithAdditionalDetailsToDelete}
          onEditTracking={props.onEditSingleTrainingTracking}
          onDeleteTracking={props.setSingleTrainingWithTrackingToDelete}
        />
      </Box>
    )
  }

  return training.isSeries ? seriesExpandedContent() : singleExpandedContent()
}
